import type { Questionnaire } from '@medplum/fhirtypes'
import {
  getComputationAnswerOptions,
  getComputationQuestionnaireItem,
  VALIDATED_TOOL_SUMMARY_ITEM,
  VALIDATED_TOOL_TAG,
} from '#src/models/questionnaires/utils/index.ts'
import { QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL } from '#src/structureDefinitions.ts'

const BINARY_ANSWER_OPTIONS = getComputationAnswerOptions([
  { code: 'incorrect', score: 0 },
  { code: 'correct', score: 1 },
])

export const mmseQuestionnaire = {
  resourceType: 'Questionnaire',
  url: 'https://www.seenhealth.org/fhir/Questionnaire/MMSE',
  title: 'Mini-Mental State Examination (MMSE)',
  name: 'MMSE',
  status: 'active',
  subjectType: ['Patient'],
  code: [
    {
      system: 'http://loinc.org',
      code: '72106-8',
      display: 'MMSE Total Score',
    },
  ],
  meta: {
    tag: [VALIDATED_TOOL_TAG],
  },
  item: [
    {
      linkId: 'instructions',
      type: 'display',
      text: '30-point cognitive screening tool testing orientation, registration, attention, recall, and language',
    },
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
      type: 'display',
      text: 'MMSE PDF',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
          valueString:
            'https://cgatoolkit.ca/Uploads/ContentDocuments/MMSE.pdf',
        },
      ],
    },
    {
      linkId: 'questions',
      type: 'group',
      item: [
        {
          linkId: 'orientation-time',
          type: 'group',
          text: 'Orientation to Time',
          item: [
            {
              linkId: 'orientation-time-year',
              type: 'choice',
              text: 'Year',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'orientation-time-season',
              type: 'choice',
              text: 'Season',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'orientation-time-date',
              type: 'choice',
              text: 'Date',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'orientation-time-day',
              type: 'choice',
              text: 'Day of week',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'orientation-time-month',
              type: 'choice',
              text: 'Month',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
          ],
        },
        {
          linkId: 'orientation-place',
          type: 'group',
          text: 'Orientation to Place',
          item: [
            {
              linkId: 'orientation-place-state',
              type: 'choice',
              text: 'State',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'orientation-place-county',
              type: 'choice',
              text: 'County',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'orientation-place-town',
              type: 'choice',
              text: 'Town/City',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'orientation-place-hospital',
              type: 'choice',
              text: 'Hospital/Building',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'orientation-place-floor',
              type: 'choice',
              text: 'Floor',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
          ],
        },
        {
          linkId: 'registration',
          type: 'group',
          text: 'Registration',
          item: [
            {
              linkId: 'registration-object-1',
              type: 'choice',
              text: 'Object 1',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'registration-object-2',
              type: 'choice',
              text: 'Object 2',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'registration-object-3',
              type: 'choice',
              text: 'Object 3',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'registration-trials',
              type: 'integer',
              text: 'Number of trials',
              required: false,
            },
          ],
        },
        {
          linkId: 'attention-calculation',
          type: 'group',
          text: 'Attention & Calculation',
          item: [
            {
              linkId: 'attention-serial-7s-1',
              type: 'choice',
              text: 'Serial 7s — 1st subtraction',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'attention-serial-7s-2',
              type: 'choice',
              text: 'Serial 7s — 2nd subtraction',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'attention-serial-7s-3',
              type: 'choice',
              text: 'Serial 7s — 3rd subtraction',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'attention-serial-7s-4',
              type: 'choice',
              text: 'Serial 7s — 4th subtraction',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'attention-serial-7s-5',
              type: 'choice',
              text: 'Serial 7s — 5th subtraction',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
          ],
        },
        {
          linkId: 'recall',
          type: 'group',
          text: 'Recall',
          item: [
            {
              linkId: 'recall-object-1',
              type: 'choice',
              text: 'Object 1',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'recall-object-2',
              type: 'choice',
              text: 'Object 2',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'recall-object-3',
              type: 'choice',
              text: 'Object 3',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
          ],
        },
        {
          linkId: 'language',
          type: 'group',
          text: 'Language',
          item: [
            {
              linkId: 'language-naming-pencil',
              type: 'choice',
              text: 'Naming — Pencil',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'language-naming-watch',
              type: 'choice',
              text: 'Naming — Watch',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'language-repetition',
              type: 'choice',
              text: 'Repetition — "No ifs, ands, or buts"',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'language-command-1',
              type: 'choice',
              text: '3-stage command — Takes paper in right hand',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'language-command-2',
              type: 'choice',
              text: '3-stage command — Folds paper in half',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'language-command-3',
              type: 'choice',
              text: '3-stage command — Puts paper on floor',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'language-read-obey',
              type: 'choice',
              text: 'Read and obey — "Close your eyes"',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'language-write-sentence',
              type: 'choice',
              text: 'Write a sentence',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
            {
              linkId: 'language-copy-design',
              type: 'choice',
              text: 'Copy design (intersecting pentagons)',
              required: true,
              answerOption: BINARY_ANSWER_OPTIONS,
            },
          ],
        },
        {
          linkId: 'level-of-consciousness',
          type: 'choice',
          text: 'Level of Consciousness',
          required: false,
          answerOption: [
            { valueCoding: { code: 'alert', display: 'Alert' } },
            { valueCoding: { code: 'drowsy', display: 'Drowsy' } },
            { valueCoding: { code: 'stupor', display: 'Stupor' } },
            { valueCoding: { code: 'coma', display: 'Coma' } },
          ],
        },
      ],
    },
    getComputationQuestionnaireItem([
      { code: 'severe-impairment', score: 9 },
      { code: 'moderate-impairment', score: 17 },
      { code: 'mild-impairment', score: 23 },
      { code: 'normal', score: 30 },
    ]),
    VALIDATED_TOOL_SUMMARY_ITEM,
  ],
} satisfies Questionnaire
